import React, { useState, useContext } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { AuthContext } from "../../App";
import PropertiesManager from "./PropertiesManager";
import ContentManager from "./ContentManager";
import MenuManager from "./MenuManager";
import ContactsManager from "./ContactsManager";
import ContractsManager from "./ContractsManager";
import ChangePassword from "./ChangePassword";
import Dashboard from "./Dashboard";
import AboutVideosManager from "./AboutVideosManager";
import CallsManager from "./CallsManager";
import CercaManager from "./CercaManager";

const CMS = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/cms', icon: '📊' },
    { name: 'Prospeccio', href: '/cms/cerca', icon: '🔍' },
    { name: 'Propiedades', href: '/cms/properties', icon: '🏠' },
    { name: 'Contenido', href: '/cms/content', icon: '📝' },
    { name: 'Videos Sobre Nosotros', href: '/cms/about-videos', icon: '🎬' },
    { name: 'Menú', href: '/cms/menu', icon: '🗂️' },
    { name: 'Contactos', href: '/cms/contacts', icon: '📧' },
    { name: 'Contratos y Firmas', href: '/cms/contracts', icon: '✍️' },
    { name: 'Llamadas IA', href: '/cms/calls', icon: '📞' },
    { name: 'Cambiar Password', href: '/cms/change-password', icon: '🔒' },
  ];

  const isActive = (href) => {
    if (href === '/cms') {
      return location.pathname === '/cms';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0`}>
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-yellow-400 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">V</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900">CMS</h1>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <nav className="mt-5 px-2 flex-1 pb-32 overflow-y-auto">
          <div className="space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`${
                  isActive(item.href)
                    ? 'bg-purple-100 text-purple-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors`}
              >
                <span className="mr-3 text-lg flex-shrink-0">{item.icon}</span>
                <span className="truncate">{item.name}</span>
              </Link>
            ))}
          </div>
        </nav>

        <div className="absolute bottom-0 w-full p-4 border-t bg-white">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">
                {user?.username?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.username}</p>
              <p className="text-xs text-gray-500 truncate">Administrador</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="truncate">Cerrar Sesión</span>
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 min-h-screen">
        <div className="sticky top-0 z-40 lg:hidden">
          <div className="flex items-center justify-between bg-white border-b px-4 py-2">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h2 className="text-lg font-semibold text-gray-900">CMS Vivendius</h2>
            <Link
              to="/cms/contracts"
              className="text-purple-600 hover:text-purple-800 transition-colors"
              title="Ir a Contratos"
            >
              <span className="text-xl">✍️</span>
            </Link>
          </div>
        </div>

        <main className="pt-2 pb-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/properties" element={<PropertiesManager />} />
              <Route path="/content" element={<ContentManager />} />
              <Route path="/about-videos" element={<AboutVideosManager />} />
              <Route path="/menu" element={<MenuManager />} />
              <Route path="/contacts" element={<ContactsManager />} />
              <Route path="/contracts" element={<ContractsManager />} />
              <Route path="/calls" element={<CallsManager />} />
              <Route path="/cerca" element={<CercaManager />} />
              <Route path="/change-password" element={<ChangePassword />} />
            </Routes>
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default CMS;